import { useState } from 'react';

const PRESETS = [15, 30, 45] as const;

export type PracticeTimeLimitControlProps = {
  maxMinutes: number;
  valueMinutes: number;
  onChangeMinutes: (minutes: number) => void;
  onSavePreference?: () => void | Promise<void>;
  preferenceSaving?: boolean;
  preferenceSaved?: boolean;
  preferenceError?: string | null;
  savePreferenceLabel?: string;
  idPrefix?: string;
};

export function PracticeTimeLimitControl({
  maxMinutes,
  valueMinutes,
  onChangeMinutes,
  onSavePreference,
  preferenceSaving = false,
  preferenceSaved = false,
  preferenceError = null,
  savePreferenceLabel = 'Save as my default for new practices',
  idPrefix = 'practice-time-limit',
}: PracticeTimeLimitControlProps) {
  const safeMax = Math.max(1, maxMinutes);
  const minutes = Math.min(safeMax, Math.max(1, Math.round(valueMinutes)));
  const visiblePresets = PRESETS.filter((p) => p <= safeMax);
  const isPresetValue = visiblePresets.includes(minutes as (typeof PRESETS)[number]);

  // Tracks whether the user has explicitly entered custom mode, independently of
  // whether the committed value happens to match a preset.
  const [customMode, setCustomMode] = useState(!isPresetValue);
  // null = not editing; string = raw draft text while the input is focused
  const [draft, setDraft] = useState<string | null>(null);

  const showCustomInput = customMode || !isPresetValue;

  function commitDraft(raw: string) {
    setDraft(null);
    const n = parseInt(raw, 10);
    if (isNaN(n)) {
      // revert: keep the current committed value
      onChangeMinutes(minutes);
      return;
    }
    onChangeMinutes(Math.min(safeMax, Math.max(1, n)));
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {visiblePresets.map((p) => (
          <button
            key={p}
            type="button"
            id={`${idPrefix}-preset-${p}`}
            onClick={() => {
              setCustomMode(false);
              setDraft(null);
              onChangeMinutes(p);
            }}
            className={`px-3 sm:px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              minutes === p && !showCustomInput
                ? 'border-green-600 bg-green-50 text-green-800'
                : 'border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            {p} min
          </button>
        ))}
        <button
          type="button"
          id={`${idPrefix}-custom`}
          onClick={() => {
            setCustomMode(true);
            // Seed draft with current value so the user can edit from where they are
            setDraft(String(minutes));
          }}
          className={`px-3 sm:px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
            showCustomInput
              ? 'border-green-600 bg-green-50 text-green-800'
              : 'border-gray-200 text-gray-700 hover:border-gray-300'
          }`}
        >
          Custom (up to {safeMax} min)
        </button>
      </div>
      {showCustomInput && (
        <div className="relative inline-flex items-center gap-2">
          <label htmlFor={`${idPrefix}-input`} className="sr-only">
            Custom time limit in minutes
          </label>
          <input
            id={`${idPrefix}-input`}
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            min={1}
            max={safeMax}
            value={draft ?? String(minutes)}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={(e) => {
              if (draft === null) setDraft(e.target.value);
            }}
            onBlur={() => commitDraft(draft ?? String(minutes))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            }}
            className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <span className="text-sm text-gray-500">minutes</span>
        </div>
      )}
      {onSavePreference && (
        <div className="pt-1 space-y-2">
          <button
            type="button"
            onClick={() => void onSavePreference()}
            disabled={preferenceSaving}
            className="text-sm font-medium text-green-800 hover:text-green-900 disabled:opacity-50"
          >
            {preferenceSaving ? 'Saving…' : savePreferenceLabel}
          </button>
          {preferenceSaved && (
            <p className="text-xs text-green-700" role="status">
              Saved.
            </p>
          )}
          {preferenceError && (
            <p className="text-xs text-red-700" role="alert">
              {preferenceError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
